import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, onSnapshot, addDoc, updateDoc,
  doc, serverTimestamp, getDoc
} from 'firebase/firestore';
import {
  Box, Container, Typography, Paper, Button, TextField, MenuItem,
  CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Alert, Accordion, AccordionSummary, AccordionDetails, Divider,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TvIcon from '@mui/icons-material/Tv';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LagbasLogo from '../components/LagbasLogo';
import { QRCodeSVG } from 'qrcode.react';

function generateToken() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

function getDisplayStatus(display) {
  if (!display.castId) return 'WAITING';
  const ls = display.lastSeen?.toDate?.() || null;
  if (ls && (Date.now() - ls.getTime()) < 120000) return 'ONLINE';
  return 'OFFLINE';
}

function relativeTime(date) {
  if (!date) return null;
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s sedan`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min sedan`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} tim sedan`;
  return date.toLocaleDateString('sv-SE');
}

function StatusDot({ status }) {
  const colors = { ONLINE: '#4caf50', OFFLINE: '#888', WAITING: '#FF6B00' };
  const labels = { ONLINE: 'ONLINE', OFFLINE: 'OFFLINE', WAITING: 'VÄNTAR PÅ SKÄRM' };
  const color = colors[status] || '#888';
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
      <Box sx={{
        width: 10, height: 10, borderRadius:'50%', bgcolor: color, flexShrink: 0,
        ...(status === 'ONLINE' ? {
          boxShadow: `0 0 0 0 ${color}`,
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': { boxShadow: `0 0 0 0 ${color}80` },
            '70%': { boxShadow: `0 0 0 8px ${color}00` },
            '100%': { boxShadow: `0 0 0 0 ${color}00` },
          },
        } : {}),
      }} />
      <Typography variant="caption" sx={{ color, fontWeight: 700, letterSpacing: 0.5 }}>
        {labels[status]}
      </Typography>
    </Box>
  );
}

function CopyButton({ value, label }) {
  const [done, setDone] = useState(false);
  function copy() {
    navigator.clipboard.writeText(value).catch(() => {});
    setDone(true);
    setTimeout(() => setDone(false), 2000);
  }
  return (
    <Tooltip title={done ? 'Kopierat!' : `Kopiera ${label}`}>
      <IconButton size="small" onClick={copy}>
        {done
          ? <CheckCircleIcon fontSize="small" sx={{ color:'#4caf50' }} />
          : <ContentCopyIcon fontSize="small" sx={{ color:'#aaa' }} />}
      </IconButton>
    </Tooltip>
  );
}

function ConceptGuide() {
  const origin = window.location.origin;
  return (
    <Accordion sx={{ mb: 2, border:'1px solid #e0e0e0', borderRadius:'8px !important', '&:before':{ display:'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: 2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap: 1.5 }}>
          <Typography fontSize="1.3rem">💡</Typography>
          <Box>
            <Typography fontWeight="bold">Hur fungerar Cast och Skärmar?</Typography>
            <Typography variant="caption" sx={{ color:'#888' }}>Begrepp, flöde och hur du delar bildspelet via mobil eller dator</Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2.5 }} />

        {/* Begrepp */}
        <Box sx={{ display:'grid', gridTemplateColumns: { xs:'1fr', md:'1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>📋 Cast (bildspel)</Typography>
            <Typography variant="body2" sx={{ color:'#555' }}>
              En spelningslista med slides – text, bilder, väder, avgångar m.m.
              Redigera på ett ställe, alla kopplade skärmar uppdateras direkt.
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>📺 Skärm (Display)</Typography>
            <Typography variant="body2" sx={{ color:'#555' }}>
              En fysisk TV, konferenssskärm eller projektor. Får en unik 6-teckens kod
              och spelar det Cast du kopplar till den.
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 0.5 }}>🔗 URL = identiteten</Typography>
            <Typography variant="body2" sx={{ color:'#555' }}>
              Varje skärm har en URL: <code style={{ background:'#f5f5f5', padding:'1px 5px', borderRadius:3, fontSize:'0.8rem' }}>{origin}/display/KOD</code>.
              Den kan öppnas på hur många enheter som helst – TV, dator, mobil, surfplatta.
            </Typography>
          </Paper>
        </Box>

        {/* Hur många enheter */}
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, bgcolor:'#fffde7', borderColor:'#ffe082' }}>
          <Typography fontWeight="bold" sx={{ mb: 1 }}>📌 Viktigt att förstå – en URL, obegränsat antal enheter</Typography>
          <Typography variant="body2" sx={{ color:'#555', mb: 1.5 }}>
            Du behöver <strong>inte</strong> en unik kod per enhet. Samma URL kan öppnas på TV:n i konferensrummet,
            en laptop på skrivbordet och en mobil i fickan – alla visar exakt samma bildspel i realtid.
            Du behöver bara en ny kod om du vill visa <em>annat</em> innehåll.
          </Typography>
          <Typography variant="body2" sx={{ color:'#555' }}>
            <strong>Exempelupplägg:</strong>
          </Typography>
          <Box sx={{ mt: 1, display:'flex', flexDirection:'column', gap: 0.75 }}>
            <Box sx={{ display:'flex', alignItems:'center', gap: 1, flexWrap:'wrap' }}>
              <Chip label='Cast "Kontoret" (kod ABC123)' size="small" sx={{ bgcolor:'#FF6B0020', color:'#cc4400', fontWeight:'bold' }} />
              <Typography variant="body2" sx={{ color:'#888' }}>→</Typography>
              <Chip label="3 st kontorsskärmar" size="small" variant="outlined" />
              <Chip label="valfritt antal mobiler" size="small" variant="outlined" />
              <Chip label="laptop i receptionen" size="small" variant="outlined" />
            </Box>
            <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
              <Chip label='Cast "Reception" (kod DEF456)' size="small" sx={{ bgcolor:'#1565C020', color:'#1565C0', fontWeight:'bold' }} />
              <Typography variant="body2" sx={{ color:'#888' }}>→</Typography>
              <Chip label="receptionsmonitor" size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Mobil och PC */}
        <Typography variant="subtitle2" sx={{ mb: 1.5, color:'#444' }}>Dela bildspelet med mobil eller dator</Typography>
        <Box sx={{ display:'grid', gridTemplateColumns: { xs:'1fr', md:'1fr 1fr' }, gap: 2, mb: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 1 }}>📱 Via QR-kod (enklast)</Typography>
            <Typography variant="body2" sx={{ color:'#555' }}>
              Klicka på <strong>Visa QR-kod</strong> på skärmkortet nedan. Skanna med telefonen – bildspelet öppnas direkt i webbläsaren.
              Vill du ha det som en app: tryck <em>Dela → Lägg till på hemskärmen</em> (iOS) eller <em>Meny → Lägg till på startskärmen</em> (Android).
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight="bold" sx={{ mb: 1 }}>💻 Via URL (dator eller mobil)</Typography>
            <Typography variant="body2" sx={{ color:'#555', mb: 1 }}>
              Öppna valfri webbläsare och gå till:
            </Typography>
            <Box sx={{ bgcolor:'#1a1a1a', borderRadius: 1, px: 1.5, py: 0.75 }}>
              <Typography sx={{ color:'#FF6B00', fontFamily:'monospace', fontSize:'0.82rem' }}>
                {origin}/display/<strong>KOD</strong>
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color:'#888', mt: 1, fontSize:'0.8rem' }}>
              Tryck F11 (Windows) eller Ctrl+Cmd+F (Mac) för helskärm.
            </Typography>
          </Paper>
        </Box>

        {/* QR-slide tips */}
        <Alert severity="success" icon={false} sx={{ fontSize:'0.82rem' }}>
          <strong>Tips:</strong> Lägg till sliden <em>"QR-kod – dela bildspel"</em> i ditt Cast. Den visar en stor QR-kod
          med din logotyp och en kort instruktion – perfekt att sätta på en presentationsskärm eller i ett mötesrum så att
          deltagare enkelt kan ta med sig innehållet till sin mobil.
        </Alert>
      </AccordionDetails>
    </Accordion>
  );
}

function SetupGuide() {
  const origin = window.location.origin;
  return (
    <Accordion sx={{ mb: 3, border:'1px solid #e0e0e0', borderRadius:'8px !important', '&:before':{ display:'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderRadius: 2 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap: 1.5 }}>
          <Typography fontSize="1.3rem">📺</Typography>
          <Box>
            <Typography fontWeight="bold">Hur ansluter jag en skärm?</Typography>
            <Typography variant="caption" sx={{ color:'#888' }}>Steg-för-steg guide för konferenssskärm, TV eller Raspberry Pi</Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Step 1 */}
        <Step n={1} title="Skapa en ny skärm här">
          Klicka <strong>Ny skärm</strong> uppe till höger och ge den ett namn (t.ex. <em>Reception</em> eller <em>Konferensrum A</em>).
          En unik 6-teckens skärmkod genereras automatiskt, t.ex. <code style={{ background:'#f0f0f0', padding:'2px 6px', borderRadius:4 }}>ABC123</code>
        </Step>

        {/* Step 2 */}
        <Step n={2} title="Öppna URL:en på skärmen">
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Navigera till följande URL i webbläsaren på skärmen — byt ut <strong>ABC123</strong> mot din skärmkod:
          </Typography>
          <Box sx={{ display:'flex', alignItems:'center', gap: 1, bgcolor:'#1a1a1a', borderRadius: 1, px: 2, py: 1, mb: 2 }}>
            <Typography sx={{ color:'#FF6B00', fontFamily:'monospace', fontSize:'0.95rem', flex:1 }}>
              {origin}/display/<strong>ABC123</strong>
            </Typography>
          </Box>

          <Box sx={{ display:'flex', flexDirection:'column', gap: 1.5 }}>
            <DeviceInstructions
              icon="📺"
              title="Samsung/LG/Sony smart-TV eller konferenssskärm med inbyggd webbläsare"
              steps={[
                'Öppna skärmens inbyggda webbläsare (ofta via hemmenyn)',
                `Skriv in URL:en: ${origin}/display/DIN-KOD`,
                'Sätt webbläsaren i helskärmsläge (F11 eller skärmens fullscreen-knapp)',
              ]}
            />
            <DeviceInstructions
              icon="🍓"
              title="Raspberry Pi (rekommenderas för permanent installation)"
              steps={[
                'Anslut Raspberry Pi till skärmen via HDMI',
                'Öppna terminalen och kör: sudo raspi-config → Boot to Desktop',
                `Lägg till i /etc/xdg/lxsession/LXDE-pi/autostart:\n@chromium-browser --kiosk --noerrdialogs --disable-infobars ${origin}/display/DIN-KOD`,
                'Starta om: sudo reboot',
              ]}
            />
            <DeviceInstructions
              icon="💻"
              title="Windows/Mac + Chrome (för test eller tillfällig skärm)"
              steps={[
                `Öppna Chrome och gå till: ${origin}/display/DIN-KOD`,
                'Tryck F11 för helskärm (Windows) eller Ctrl+Cmd+F (Mac)',
                'Tips: Högerklicka → Cast för att spegla till Chromecast-ansluten TV',
              ]}
            />
          </Box>
        </Step>

        {/* Step 3 */}
        <Step n={3} title="Koppla ett Cast till skärmen">
          Tillbaka på den här sidan: välj ett <strong>Cast</strong> (spelningslista) i dropdownen på skärmkortet.
          Innehållet börjar spelas direkt och statusen ändras till <strong style={{ color:'#4caf50' }}>ONLINE</strong>.
        </Step>

        {/* Step 4 */}
        <Step n={4} title="Klart!" last>
          Skärmen uppdateras automatiskt när du ändrar anslag eller skapar nytt innehåll — ingen omstart behövs.
        </Step>
      </AccordionDetails>
    </Accordion>
  );
}

function Step({ n, title, children, last }) {
  return (
    <Box sx={{ display:'flex', gap: 2, mb: last ? 0 : 3 }}>
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
        <Box sx={{ width:32, height:32, borderRadius:'50%', bgcolor:'#FF6B00', color:'white',
          display:'flex', alignItems:'center', justifyContent:'center', fontWeight:900, fontSize:'0.9rem' }}>
          {n}
        </Box>
        {!last && <Box sx={{ width:2, flex:1, bgcolor:'#f0f0f0', mt:1 }} />}
      </Box>
      <Box sx={{ flex:1, pb: last ? 0 : 2 }}>
        <Typography fontWeight="bold" sx={{ mb:0.75 }}>{title}</Typography>
        <Typography variant="body2" sx={{ color:'#555', lineHeight:1.6 }}>{children}</Typography>
      </Box>
    </Box>
  );
}

function DeviceInstructions({ icon, title, steps }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{icon} {title}</Typography>
      <Box component="ol" sx={{ m:0, pl:2.5 }}>
        {steps.map((s, i) => (
          <Typography key={i} component="li" variant="body2" sx={{ color:'#555', mb:0.5, whiteSpace:'pre-wrap' }}>{s}</Typography>
        ))}
      </Box>
    </Paper>
  );
}

function QRDialog({ open, onClose, display, logoUrl }) {
  const displayUrl = `${window.location.origin}/display/${display?.token}`;
  const logoSrc = logoUrl || '/logo192.png';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ textAlign:'center', pb: 0 }}>
        Skanna för att öppna bildspelet
      </DialogTitle>
      <DialogContent sx={{ display:'flex', flexDirection:'column', alignItems:'center', gap: 2, pt: 2 }}>
        <Box sx={{ p: 2, bgcolor:'white', borderRadius: 2, border:'1px solid #e0e0e0' }}>
          <QRCodeSVG
            value={displayUrl}
            size={200}
            level="H"
            imageSettings={{
              src: logoSrc,
              x: undefined,
              y: undefined,
              height: 44,
              width: 44,
              excavate: true,
            }}
          />
        </Box>
        <Box sx={{ textAlign:'center' }}>
          <Typography variant="caption" sx={{ color:'#888', display:'block', mb: 0.5 }}>
            {display?.name}
          </Typography>
          <Typography sx={{ fontFamily:'monospace', fontWeight:'bold', fontSize:'1.3rem', letterSpacing: 4, color:'#FF6B00' }}>
            {display?.token}
          </Typography>
        </Box>
        <Alert severity="info" sx={{ width:'100%', fontSize:'0.8rem' }}>
          <strong>Installera som app:</strong> Öppna URL:en i webbläsaren och välj
          <em> Dela → Lägg till på hemskärmen</em> (iOS) eller
          <em> Meny → Lägg till på startskärmen</em> (Android/Chrome).
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
}

function NewDisplayDialog({ open, onClose, onSave }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  async function handleSave() {
    setSaving(true);
    await onSave(name.trim());
    setName(''); setSaving(false);
  }
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Ny skärm</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth label="Skärmnamn" value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && name.trim() && handleSave()}
          sx={{ mt: 1 }} helperText="En 6-tecken kod genereras automatiskt" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" disabled={!name.trim() || saving}
          sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}
          onClick={handleSave}>
          {saving ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Skapa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CopyLayoutDialog({ open, onClose, casts }) {
  const [sourceCastId, setSourceCastId] = useState('');
  const [targetIds, setTargetIds] = useState([]);
  const [copying, setCopying] = useState(false);
  const [done, setDone] = useState(false);

  async function handleCopy() {
    if (!sourceCastId || !targetIds.length) return;
    setCopying(true);
    try {
      const source = casts.find(c => c.id === sourceCastId);
      if (!source) return;
      for (const tid of targetIds) {
        await updateDoc(doc(db, 'casts', tid), { slides: source.slides || [] });
      }
      setDone(true);
    } catch(e) { console.error(e); }
    finally { setCopying(false); }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Kopiera layout</DialogTitle>
      <DialogContent>
        {done && <Alert severity="success" sx={{ mb: 2 }}>Layout kopierad!</Alert>}
        <TextField select fullWidth label="Källcast" value={sourceCastId} onChange={e => setSourceCastId(e.target.value)} sx={{ mt: 1, mb: 2 }}>
          {casts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <Typography variant="body2" sx={{ mb: 1, color:'#666' }}>Välj målcast(s):</Typography>
        {casts.filter(c => c.id !== sourceCastId).map(c => (
          <Box key={c.id} sx={{ display:'flex', alignItems:'center', gap: 1, py: 0.5 }}>
            <input type="checkbox" checked={targetIds.includes(c.id)}
              onChange={e => setTargetIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))} />
            <Typography>{c.name}</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
        <Button variant="contained" disabled={!sourceCastId || !targetIds.length || copying}
          sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}
          onClick={handleCopy}>
          {copying ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Kopiera'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CastNDisplays() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const companyId = userProfile?.companyId || '';
  const uid = currentUser?.uid || '';

  const [displays, setDisplays] = useState([]);
  const [casts, setCasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDisplayOpen, setNewDisplayOpen] = useState(false);
  const [copyLayoutOpen, setCopyLayoutOpen] = useState(false);
  const [qrDisplay, setQrDisplay] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);

  // Re-render every 30s to update relative timestamps + status dots
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!companyId) return;
    const q1 = query(collection(db, 'displays'), where('companyId', '==', companyId));
    const unsub1 = onSnapshot(q1, snap => {
      setDisplays(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    const q2 = query(collection(db, 'casts'), where('companyId', '==', companyId));
    const unsub2 = onSnapshot(q2, snap => {
      setCasts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => { unsub1(); unsub2(); };
  }, [companyId]);

  // Fetch company logo for QR code
  useEffect(() => {
    if (!companyId) return;
    getDoc(doc(db, 'companies', companyId)).then(snap => {
      if (snap.exists()) setCompanyLogo(snap.data().logo || null);
    });
  }, [companyId]);

  async function handleCreateDisplay(name) {
    const token = generateToken();
    await addDoc(collection(db, 'displays'), {
      name, token, castId: null, status: 'waiting',
      companyId, createdBy: uid, createdAt: serverTimestamp(),
    });
    setNewDisplayOpen(false);
  }

  async function handleChangeCast(displayId, castId) {
    await updateDoc(doc(db, 'displays', displayId), {
      castId: castId || null,
      status: castId ? 'connected' : 'waiting',
    });
  }

  async function handleChangeLayout(displayId, layout) {
    await updateDoc(doc(db, 'displays', displayId), { displayLayout: layout });
  }

  async function handleChangeSidfotColumns(displayId, n) {
    await updateDoc(doc(db, 'displays', displayId), { sidfotColumns: n });
  }

  async function handleChangeSidoRows(displayId, n) {
    await updateDoc(doc(db, 'displays', displayId), { sidoRows: n });
  }

  async function handleChangeLogoOverlay(displayId, show) {
    await updateDoc(doc(db, 'displays', displayId), { showLogoOverlay: show });
  }

  async function handleChangePanelStyle(displayId, style) {
    await updateDoc(doc(db, 'displays', displayId), { panelStyle: style });
  }

  const onlineCount = displays.filter(d => getDisplayStatus(d) === 'ONLINE').length;

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1a1a1a', color:'white', px: 3, py: 2, display:'flex', alignItems:'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <Typography variant="h6" fontWeight="bold">
          Cast<span style={{ color:'#FF6B00' }}>N</span>
          <span style={{ color:'#aaa', fontWeight:400, fontSize:'0.9rem', marginLeft:8 }}>– Hantera Skärmar</span>
        </Typography>
        {!loading && displays.length > 0 && (
          <Chip
            label={`${onlineCount} / ${displays.length} online`}
            size="small"
            sx={{ bgcolor: onlineCount > 0 ? '#4caf50' : '#555', color:'white', ml:1, fontWeight:'bold' }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ArrowBackIcon />} sx={{ color:'#aaa' }} onClick={() => navigate(-1)}>
          Tillbaka
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Concept guide */}
        <ConceptGuide />

        {/* Setup guide */}
        <SetupGuide />

        {/* Action bar */}
        <Box sx={{ display:'flex', justifyContent:'flex-end', gap: 2, mb: 3 }}>
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => setCopyLayoutOpen(true)}
            sx={{ borderColor:'#2196F3', color:'#2196F3' }}>
            Kopiera layout
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewDisplayOpen(true)}
            sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
            Ny skärm
          </Button>
        </Box>

        {loading && <Box sx={{ textAlign:'center', py: 6 }}><CircularProgress sx={{ color:'#FF6B00' }} /></Box>}

        {!loading && displays.length === 0 && (
          <Paper sx={{ p: 6, textAlign:'center' }}>
            <TvIcon sx={{ fontSize:60, color:'#ddd', mb: 2 }} />
            <Typography variant="h6" sx={{ color:'#aaa', mb:1 }}>Inga skärmar än</Typography>
            <Typography variant="body2" sx={{ color:'#bbb', mb: 3 }}>
              Klicka "Ny skärm" och följ guiden ovan för att sätta upp din första informationsskärm.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNewDisplayOpen(true)}
              sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
              Ny skärm
            </Button>
          </Paper>
        )}

        {/* Display cards */}
        <Box sx={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(420px, 1fr))', gap: 2.5 }}>
          {displays.map(d => {
            const status = getDisplayStatus(d);
            const ls = d.lastSeen?.toDate?.() || null;
            const castName = casts.find(c => c.id === d.castId)?.name;
            const displayUrl = `${window.location.origin}/display/${d.token}`;

            return (
              <Paper key={d.id} sx={{
                p: 0, overflow:'hidden', borderRadius: 2,
                border: status === 'ONLINE' ? '2px solid #4caf5040' : '2px solid transparent',
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.12)' },
              }}>
                {/* Card header */}
                <Box sx={{ bgcolor: status === 'ONLINE' ? '#f0fdf4' : status === 'WAITING' ? '#fff9f5' : '#fafafa',
                  px: 2.5, py: 2, display:'flex', alignItems:'center', gap: 1.5,
                  borderBottom:'1px solid #f0f0f0' }}>
                  <TvIcon sx={{ color: status === 'ONLINE' ? '#4caf50' : '#bbb', fontSize:28 }} />
                  <Box sx={{ flex:1, minWidth:0 }}>
                    <Typography fontWeight="bold" fontSize="1.1rem" noWrap>{d.name}</Typography>
                    {ls && (
                      <Typography variant="caption" sx={{ color:'#aaa' }}>Senast sedd {relativeTime(ls)}</Typography>
                    )}
                  </Box>
                  <StatusDot status={status} />
                </Box>

                {/* Card body */}
                <Box sx={{ px: 2.5, py: 2, display:'flex', flexDirection:'column', gap: 2 }}>
                  {/* Token + URL */}
                  <Box>
                    <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Skärmkod</Typography>
                    <Box sx={{ display:'flex', alignItems:'center', gap: 1 }}>
                      <Box sx={{ bgcolor:'#1a1a1a', color:'#FF6B00', px:2, py:0.75, borderRadius:1,
                        fontFamily:'monospace', fontWeight:'bold', letterSpacing:5, fontSize:'1.3rem' }}>
                        {d.token}
                      </Box>
                      <CopyButton value={d.token} label="koden" />
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>QR-kod</Typography>
                    <Button size="small" variant="outlined" startIcon={<QrCode2Icon />}
                      onClick={() => setQrDisplay(d)}
                      sx={{ borderColor:'#ddd', color:'#555', textTransform:'none' }}>
                      Visa QR-kod
                    </Button>
                  </Box>

                  <Box>
                    <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Skärm-URL</Typography>
                    <Box sx={{ display:'flex', alignItems:'center', gap: 0.5, bgcolor:'#f8f8f8', borderRadius:1,
                      px:1.5, py:0.75, border:'1px solid #e8e8e8' }}>
                      <Typography variant="body2" sx={{ flex:1, fontFamily:'monospace', fontSize:'0.8rem', color:'#555', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {displayUrl}
                      </Typography>
                      <CopyButton value={displayUrl} label="URL:en" />
                      <Tooltip title="Öppna i ny flik">
                        <IconButton size="small" component="a" href={displayUrl} target="_blank" rel="noopener">
                          <OpenInNewIcon fontSize="small" sx={{ color:'#aaa' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Divider />

                  {/* Cast selector */}
                  <Box sx={{ display:'flex', gap: 2 }}>
                    <Box sx={{ flex:1 }}>
                      <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Kopplat Cast</Typography>
                      <TextField select size="small" fullWidth value={d.castId || ''} onChange={e => handleChangeCast(d.id, e.target.value)}>
                        <MenuItem value=""><em>– Inget –</em></MenuItem>
                        {casts.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                      </TextField>
                    </Box>
                    <Box sx={{ flex:1 }}>
                      <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Layout</Typography>
                      <TextField select size="small" fullWidth value={d.displayLayout || 'fullscreen'} onChange={e => handleChangeLayout(d.id, e.target.value)}>
                        <MenuItem value="fullscreen">Helskärm</MenuItem>
                        <MenuItem value="two-zone">2-zon (70/30)</MenuItem>
                        <MenuItem value="three-zone">3-zon</MenuItem>
                      </TextField>
                    </Box>
                  </Box>

                  {/* Zone config – only relevant for multi-zone layouts */}
                  {(d.displayLayout === 'two-zone' || d.displayLayout === 'three-zone') && (
                    <Box>
                      <Typography variant="caption" sx={{ color:'#888', display:'block', mb:0.5 }}>Zonkonfiguration</Typography>
                      <Box sx={{ display:'flex', gap: 1.5, flexWrap:'wrap', alignItems:'center' }}>
                        <TextField select size="small" label="Sidfot – kolumner"
                          value={d.sidfotColumns || 1}
                          onChange={e => handleChangeSidfotColumns(d.id, Number(e.target.value))}
                          sx={{ width: 150 }}>
                          <MenuItem value={1}>1 kolumn</MenuItem>
                          <MenuItem value={2}>2 kolumner</MenuItem>
                          <MenuItem value={3}>3 kolumner</MenuItem>
                          <MenuItem value={4}>4 kolumner</MenuItem>
                        </TextField>
                        {(d.displayLayout === 'two-zone' || d.displayLayout === 'three-zone') && (
                          <TextField select size="small" label="Sidofält – rader"
                            value={d.sidoRows || 1}
                            onChange={e => handleChangeSidoRows(d.id, Number(e.target.value))}
                            sx={{ width: 150 }}>
                            <MenuItem value={1}>1 rad (hel)</MenuItem>
                            <MenuItem value={2}>2 rader</MenuItem>
                            <MenuItem value={3}>3 rader</MenuItem>
                          </TextField>
                        )}
                        <TextField select size="small" label="Panelstil"
                          value={d.panelStyle || 'classic'}
                          onChange={e => handleChangePanelStyle(d.id, e.target.value)}
                          sx={{ width: 150 }}>
                          <MenuItem value="classic">Klassisk (helyta)</MenuItem>
                          <MenuItem value="glass">Glasblock (m. bakgrund)</MenuItem>
                        </TextField>
                        <Tooltip title="Visa företagslogotyp uppe till vänster ovanpå bildspelet">
                          <Chip
                            label={d.showLogoOverlay ? 'Logo: på' : 'Logo: av'}
                            size="small"
                            onClick={() => handleChangeLogoOverlay(d.id, !d.showLogoOverlay)}
                            sx={{ cursor:'pointer', bgcolor: d.showLogoOverlay ? '#FF6B00' : '#e0e0e0',
                              color: d.showLogoOverlay ? 'white' : '#555' }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                  )}

                  {castName && (
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Chip label={`▶ ${castName}`} size="small" sx={{ bgcolor:'#FF6B0020', color:'#cc4400', fontWeight:'bold' }} />
                      {status === 'ONLINE' && <Typography variant="caption" sx={{ color:'#4caf50' }}>Spelar just nu</Typography>}
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Container>

      <NewDisplayDialog open={newDisplayOpen} onClose={() => setNewDisplayOpen(false)} onSave={handleCreateDisplay} />
      <CopyLayoutDialog open={copyLayoutOpen} onClose={() => setCopyLayoutOpen(false)} casts={casts} />
      <QRDialog open={Boolean(qrDisplay)} onClose={() => setQrDisplay(null)} display={qrDisplay} logoUrl={companyLogo} />
    </Box>
  );
}
